import { Injectable, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';
import { NativeAudio } from '@awesome-cordova-plugins/native-audio/ngx';
import { AnalyticsService } from './analytics.service';

@Injectable({ providedIn: 'root' })
export class SoundService implements OnDestroy {
  private readonly LEARNING_START_ID = 'learning_start';
  private readonly SOUND_PATH = 'assets/sounds/elephant_trumpet.mp3';
  private readonly UNLOCK_ID = 'learning_unlock';
  private readonly UNLOCK_PATH = 'assets/sounds/unlock.mp3';
  private isPreloaded = false;
  private unlockRequested = false;
  private destroyed = false;
  private removeUnlockGesture?: () => void;
  private readonly cr7Preloaded = new Set<string>();

  constructor(
    private platform: Platform,
    private nativeAudio: NativeAudio,
    private analytics: AnalyticsService
  ) {
    this.initializeAudio();
  }

  /**
   * Initialize and preload audio when platform is ready
   */
  private async initializeAudio(): Promise<void> {
    try {
      await this.platform.ready();

      if (this.platform.is('cordova')) {
        // Native audio for mobile (iOS/Android)
        await this.preloadNativeAudio();
      } else {
        // Fallback to HTML5 for web/browser
        console.log('Running on web - HTML5 audio fallback available');
      }
    } catch (error) {
      console.warn('Audio initialization failed:', error);
    }
  }

  /**
   * Preload audio file for instant playback
   */
  private async preloadNativeAudio(): Promise<void> {
    if (this.isPreloaded) {
      return;
    }

    try {
      await this.nativeAudio.preloadSimple(
        this.LEARNING_START_ID,
        this.SOUND_PATH
      );
      this.isPreloaded = true;
      this.cr7Preloaded.add(this.LEARNING_START_ID);
      console.log('Native audio preloaded successfully');
    } catch (error) {
      console.error('Failed to preload native audio:', error);
    }
  }

  /**
   * Play learning start sound effect
   * Uses native audio on mobile, HTML5 fallback on web
   */
  playLearningStart(sound: 'elephant' | 'trumpet' = 'elephant'): void {
    void this.playCr7Sound(this.LEARNING_START_ID, this.SOUND_PATH, sound);
  }

  /** Sound used by the CR7 learning launch and by the completed-elephant animation. */
  playElephant(): void { this.playLearningStart(); }

  /** The prototype uses the same trumpet asset for learning start and completion. */
  playTrumpet(): void { this.playLearningStart('trumpet'); }

  /** Unlock effect. Missing or blocked audio intentionally falls back to silence. */
  playUnlock(): void {
    if (this.unlockRequested || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.unlockRequested = true;
    void this.playCr7Sound(this.UNLOCK_ID, this.UNLOCK_PATH, 'unlock');
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.removeUnlockGesture?.();
  }

  private retryUnlockOnGesture(audio: HTMLAudioElement): void {
    if (this.destroyed) return;
    this.removeUnlockGesture?.();
    const events = ['pointerdown', 'pointerup', 'touchend', 'keydown'] as const;
    const remove = () => {
      events.forEach(event => document.removeEventListener(event, play, true));
      this.removeUnlockGesture = undefined;
    };
    const play = (event: Event) => {
      // Touch and pen activation arrives on release; consuming pointerdown loses the first valid gesture.
      if (event.type === 'pointerdown' && (event as PointerEvent).pointerType !== 'mouse') return;
      if (event.type === 'pointerup' && (event as PointerEvent).pointerType === 'mouse') return;
      if (event instanceof KeyboardEvent && event.key === 'Escape') return;
      remove();
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Keep play() inside the gesture callback; awaiting platform.ready() loses iOS user activation.
      void audio.play().catch(error => {
        this.analytics.log('student_sound_failed', { sound: 'unlock', error_code: AnalyticsService.errorCode(error) });
      });
    };
    this.removeUnlockGesture = remove;
    events.forEach(event => document.addEventListener(event, play, { capture: true }));
  }

  private async playCr7Sound(soundId: string, soundPath: string, sound: 'unlock' | 'elephant' | 'trumpet'): Promise<void> {
    try {
      await this.platform.ready();
      if (this.destroyed) return;
      if (this.platform.is('cordova')) {
        if (!this.cr7Preloaded.has(soundId)) {
          await this.nativeAudio.preloadSimple(soundId, soundPath);
          this.cr7Preloaded.add(soundId);
        }
        await this.nativeAudio.play(soundId);
        return;
      }
      const audio = new Audio(soundPath);
      audio.volume = 1;
      audio.preload = 'auto';
      try {
        await audio.play();
      } catch (error) {
        if (sound === 'unlock' && error instanceof DOMException && error.name === 'NotAllowedError') {
          this.retryUnlockOnGesture(audio);
        }
        throw error;
      }
    } catch (error) {
      // Audio is decorative; browser policy or a missing file must never interrupt learning.
      this.analytics.log('student_sound_failed', { sound, error_code: AnalyticsService.errorCode(error) });
    }
  }

  /**
   * Play sound using native audio (mobile)
   * Near-zero latency, no stuttering
   */
  private playNativeAudio(): void {
    if (!this.isPreloaded) {
      console.warn('Audio not preloaded yet');
      return;
    }

    try {
      this.nativeAudio.play(this.LEARNING_START_ID).then(
        () => {
          console.log('Native audio played successfully');
        },
        (error: any) => {
          console.warn('Native audio playback failed:', error);
        }
      );
    } catch (error) {
      console.error('Error playing native audio:', error);
    }
  }

  /**
   * Fallback to HTML5 audio for web/browser testing
   */
  private playHTML5Audio(): void {
    try {
      const audio = new Audio(this.SOUND_PATH);
      audio.volume = 1;

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn('HTML5 audio playback failed:', error);
        });
      }
    } catch (error) {
      console.error('Error playing HTML5 audio:', error);
    }
  }

  /**
   * Play any custom sound file
   * @param soundId - Unique identifier for the sound
   * @param soundPath - Path to the sound file
   */
  async playCustomSound(soundId: string, soundPath: string): Promise<void> {
    if (!this.platform.is('cordova')) {
      // HTML5 fallback
      const audio = new Audio(soundPath);
      audio.volume = 0.5;
      await audio.play();
      return;
    }

    try {
      // Preload if not already loaded
      await this.nativeAudio.preloadSimple(soundId, soundPath);
      await this.nativeAudio.play(soundId);
    } catch (error) {
      console.error('Error playing custom sound:', error);
    }
  }

  /**
   * Unload audio to free memory
   * Call this when audio is no longer needed
   */
  async unloadAudio(): Promise<void> {
    if (!this.platform.is('cordova') || !this.isPreloaded) {
      return;
    }

    try {
      await this.nativeAudio.unload(this.LEARNING_START_ID);
      this.isPreloaded = false;
      this.cr7Preloaded.delete(this.LEARNING_START_ID);
      console.log('Native audio unloaded');
    } catch (error) {
      console.warn('Error unloading audio:', error);
    }
  }

  /**
   * Set volume for native audio
   * @param volume - Volume level (0.0 to 1.0)
   */
  async setVolume(volume: number): Promise<void> {
    if (!this.platform.is('cordova')) {
      return;
    }

    const clampedVolume = Math.max(0, Math.min(1, volume));

    try {
      await this.nativeAudio.setVolumeForComplexAsset(
        this.LEARNING_START_ID,
        clampedVolume
      );
    } catch (error) {
      console.warn('Error setting volume:', error);
    }
  }
}
